import { useCallback, useState } from 'react';

import type { SendVerificationLinkDto, SendVerificationLinkResult } from '@identity/application/dtos';
import { useSendVerificationLink } from '@identity/presentation/hooks/useSendVerificationLink';
import { resolveIdentityErrorMessage } from '@identity/presentation/utils';
import type { IdentityErrorMessage } from '@identity/presentation/utils';

export type VerificationLinkSender = {
  /** i18n key of the inline error, or `undefined` when the last send succeeded. */
  errorKey?: string;
  /** Seconds to wait, when the failure named one. The key it comes with interpolates it. */
  errorRetryAfterSeconds?: number;
  isSending: boolean;
  /** Sends the link. It resolves the accepted verification, or `null` when the backend refused. */
  send: (dto: SendVerificationLinkDto) => Promise<SendVerificationLinkResult | null>;
};

/**
 * Sends a verification link and keeps the inline error of the last attempt.
 *
 * Both views of a channel step send links — the capture and the resend — and need the same three
 * values. It resolves instead of throwing, so a click handler needs no `try/catch`, and `null` is
 * the rejection: a caller that only cares whether it went out checks for it and ignores the rest.
 *
 * A rejected send disables nothing for a while: the rate limit arrives as a code, the step shows its
 * message, and the user can try again. The message says how long to wait when the failure names it,
 * but there is no countdown: the longest wait outlives the visit, and nobody watches a clock for it.
 */
export const useVerificationLinkSender = (): VerificationLinkSender => {
  const { mutateAsync } = useSendVerificationLink();
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<IdentityErrorMessage | undefined>(undefined);

  const send = useCallback(
    async (dto: SendVerificationLinkDto): Promise<SendVerificationLinkResult | null> => {
      setIsSending(true);
      setError(undefined);

      try {
        return await mutateAsync(dto);
      } catch (failure) {
        setError(resolveIdentityErrorMessage(failure));

        return null;
      } finally {
        setIsSending(false);
      }
    },
    [mutateAsync],
  );

  return { errorKey: error?.i18nKey, errorRetryAfterSeconds: error?.retryAfterSeconds, isSending, send };
};
